import React, { useState } from 'react';
import { useNode, useEditor } from "@craftjs/core";
import {
    FormControl,
    FormLabel,
    TextField,
    Button,
    Box,
    Typography,
    IconButton,
    Divider,
    Alert,
    Snackbar,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Paper,
    Chip,
    FormControlLabel,
    Switch,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from "@mui/material";
import {
    ShoppingCart as CartIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Remove as RemoveIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Home as AddressIcon,
    CheckCircle as ConfirmIcon
} from "@mui/icons-material";
import { useSafeCart, useSafeCartDispatch, cartActions, createOrderFromCart } from './CartContext';
import { getSelectionStyle } from '../../../Stack/utils/stackUtils';

export const Cart = ({
    title = "Shopping Cart",
    showTitle = true,
    showItemCount = true,
    showCustomerForm = true,
    showOrderButton = true,
    emptyCartMessage = "Your cart is empty",
    orderButtonText = "Confirm Order",
    orderSuccessMessage = "Order confirmed successfully!",
    orderDomain = "autodinStockOrder",
    customerFormTitle = "Customer Information",
    onOrderSuccess = null, // Callback function when order is completed
    // Item display configuration
    itemProperties = [
        { field: 'designationcourte', label: 'Product Name', enabled: true },
        { field: 'code', label: 'Code', enabled: true },
        { field: 'deslong', label: 'Description', enabled: true },
        { field: 'famille', label: 'Family', enabled: true },
        { field: 'marque', label: 'Brand', enabled: false },
        { field: 'modele', label: 'Model', enabled: false },
        { field: 'brief', label: 'Brief', enabled: false },
        { field: 'category', label: 'Category', enabled: false }
    ],
    showItemImages = false,
    imageSize = "40px",
    // Styling
    width = "400px",
    maxHeight = "600px",
    backgroundColor = "#ffffff",
    borderRadius = "8px",
    padding = "16px",
    showBorder = true,
    borderColor = "#e0e0e0",
    borderWidth = "1px",
    showItemSeparators = true,
    itemSeparatorColor = "#f0f0f0",
    style = {},
    // Debug
    debug = false,
    ...props
}) => {
    const {
        connectors: { connect, drag },
        isSelected,
        isHovered,
    } = useNode((state) => ({
        isSelected: state.events.selected,
        isHovered: state.events.hovered,
    }));

    const { enabled } = useEditor((state) => ({
        enabled: state.options.enabled
    }));

    // Cart state - using safe hooks that don't throw errors
    const [mockCart, setMockCart] = useState({
        items: [
            {
                productId: 'sample-1',
                quantity: 2,
                productData: { title: 'Sample Product 1', price: '$29.99' }
            },
            {
                productId: 'sample-2',
                quantity: 1,
                productData: { title: 'Sample Product 2', price: '$19.99' }
            }
        ],
        totalItems: 3,
        customerInfo: {
            email: 'customer@example.com',
            phone: '+1234567890',
            address: '123 Main St'
        }
    });

    // Always call hooks (to satisfy React rules) - these are safe and won't throw
    const realCart = useSafeCart();
    const realDispatch = useSafeCartDispatch();
    
    // Use real cart if available, otherwise use mock
    const cart = realCart || mockCart;
    
    // Always log cart state for debugging
    console.log('[Cart] Cart state:', cart);
    console.log('[Cart] Cart items count:', cart?.items?.length || 0);
    console.log('[Cart] Debug mode:', debug);
    console.log('[Cart] Item properties config:', itemProperties);
    
    // Log detailed item data
    if (cart?.items?.length > 0) {
        cart.items.forEach((item, index) => {
            console.log(`[Cart] Item ${index} full data:`, item);
            console.log(`[Cart] Item ${index} productData:`, item.productData);
            if (item.productData) {
                console.log(`[Cart] Item ${index} available properties:`, Object.keys(item.productData));
            }
        });
    }
    
    const dispatch = realDispatch || ((action) => {
        // Mock dispatch for editor mode
        if (action.type === 'UPDATE_CUSTOMER_INFO') {
            setMockCart(prev => ({
                ...prev,
                customerInfo: { ...prev.customerInfo, ...action.payload }
            }));
        }
    });
    
    // Local state
    const [isCreatingOrder, setIsCreatingOrder] = useState(false);
    const [showOrderSuccess, setShowOrderSuccess] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [orderError, setOrderError] = useState(null);

    // Handle quantity changes
    const handleQuantityChange = (productId, newQuantity) => {
        if (dispatch) {
            if (newQuantity <= 0) {
                dispatch(cartActions.removeItem(productId));
            } else {
                dispatch(cartActions.updateQuantity(productId, newQuantity));
            }
        }
    };

    // Handle customer info changes
    const handleCustomerInfoChange = (field, value) => {
        if (dispatch) {
            dispatch(cartActions.updateCustomerInfo({ [field]: value }));
        }
    };

    // Handle order confirmation
    const handleConfirmOrder = async () => {
        if (!dispatch) {
            // In editor mode, just show a placeholder message
            setOrderError("This is a preview - order functionality works when cart is connected to a shop");
            return;
        }

        if (cart.items.length === 0) {
            setOrderError("Cart is empty");
            return;
        }

        if (!cart.customerInfo.email || !cart.customerInfo.phone) {
            setOrderError("Please provide email and phone number");
            return;
        }

        setIsCreatingOrder(true);
        setOrderError(null);

        try {
            const orderResult = await createOrderFromCart(cart);
            
            if (debug) {
                console.log('[Cart] Order created:', orderResult);
            }
            
            // Clear cart after successful order
            dispatch(cartActions.clearCart());
            
            // Show success modal
            setShowSuccessModal(true);
            
        } catch (error) {
            console.error('[Cart] Error creating order:', error);
            setOrderError("Failed to create order. Please try again.");
        } finally {
            setIsCreatingOrder(false);
        }
    };

    // Handle clear cart
    const handleClearCart = () => {
        if (dispatch) {
            dispatch(cartActions.clearCart());
        }
    };

    // Handle success modal OK button
    const handleSuccessModalOk = () => {
        setShowSuccessModal(false);
        
        // Call the onOrderSuccess callback if provided (to return to product list)
        if (onOrderSuccess) {
            onOrderSuccess();
        }
    };

    const containerStyle = {
        width,
        maxHeight,
        backgroundColor,
        borderRadius,
        padding,
        border: showBorder ? `${borderWidth} solid ${borderColor}` : 'none',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        ...style,
        ...getSelectionStyle(isSelected, isHovered, enabled)
    };

    return (
        <div
            ref={ref => enabled ? connect(drag(ref)) : connect(ref)}
            style={containerStyle}
            {...props}
        >
            {/* Cart Header */}
            {showTitle && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CartIcon color="primary" />
                    <Typography variant="h6" component="h2">
                        {title}
                    </Typography>
                    {showItemCount && (
                        <Chip 
                            label={`${cart.totalItems} items`} 
                            size="small" 
                            color="primary"
                            variant="outlined"
                        />
                    )}
                </Box>
            )}

            {/* Empty Cart Message */}
            {cart.items.length === 0 && (
                <Box sx={{ 
                    textAlign: 'center', 
                    py: 4, 
                    color: '#666',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2
                }}>
                    <CartIcon sx={{ fontSize: 48, color: '#ccc' }} />
                    <Typography variant="body1">
                        {emptyCartMessage}
                    </Typography>
                </Box>
            )}

            {/* Cart Items */}
            {cart.items.length > 0 && (
                <Box sx={{ flex: 1, mb: 2 }}>
                    <List disablePadding>
                        {cart.items.map((item, index) => {
                            if (debug) {
                                console.log(`[Cart] Rendering item ${index}:`, item);
                                console.log(`[Cart] Available properties:`, Object.keys(item.productData || {}));
                                console.log(`[Cart] Item properties config:`, itemProperties);
                            }
                            return (
                            <React.Fragment key={item.productId}>
                                <ListItem sx={{ px: 0, alignItems: 'flex-start' }}>
                                    {/* Product Image */}
                                    {showItemImages && item.productData?.image && (
                                        <Box sx={{ mr: 2, mt: 1 }}>
                                            <img 
                                                src={item.productData.image} 
                                                alt={item.productData?.title || 'Product'}
                                                style={{
                                                    width: imageSize,
                                                    height: imageSize,
                                                    objectFit: 'cover',
                                                    borderRadius: '4px'
                                                }}
                                            />
                                        </Box>
                                    )}
                                    
                                    <ListItemText
                                        primary={
                                            <Box>
                                                {debug && (
                                                    <Typography variant="caption" sx={{ color: 'blue', display: 'block', mb: 1 }}>
                                                        DEBUG: Checking {itemProperties.filter(prop => prop.enabled).length} enabled properties
                                                    </Typography>
                                                )}
                                                {/* Dynamic property display */}
                                                {itemProperties.filter(prop => prop.enabled).map((prop, propIndex) => {
                                                    // Check multiple possible locations for the property value
                                                    const value = item.productData?.[prop.field] || 
                                                                 item.productData?.qwantum?.[prop.field] ||
                                                                 item[prop.field] || 
                                                                 (prop.field === 'title' ? (item.productData?.name || item.productData?.title) : null) ||
                                                                 (prop.field === 'name' ? (item.productData?.title || item.productData?.name) : null);
                                                    
                                                    if (debug) {
                                                        console.log(`[Cart] Property ${prop.field} (${prop.label}):`, value);
                                                        console.log(`[Cart] Full item data:`, item);
                                                    }
                                                    
                                                    if (!value) {
                                                        // Show placeholder for debugging when debug is enabled
                                                        if (debug) {
                                                            return (
                                                                <Typography 
                                                                    key={propIndex}
                                                                    variant="body2" 
                                                                    component="div"
                                                                    sx={{ 
                                                                        color: 'orange',
                                                                        mb: 0.25,
                                                                        fontStyle: 'italic'
                                                                    }}
                                                                >
                                                                    <strong>{prop.label}:</strong> [No data for '{prop.field}']
                                                                </Typography>
                                                            );
                                                        }
                                                        return null;
                                                    }
                                                    
                                                    return (
                                                        <Typography 
                                                            key={propIndex}
                                                            variant={propIndex === 0 ? "body1" : "body2"} 
                                                            component="div"
                                                            sx={{ 
                                                                fontWeight: propIndex === 0 ? 'bold' : 'normal',
                                                                color: propIndex === 0 ? 'text.primary' : 'text.secondary',
                                                                mb: propIndex === 0 ? 0.5 : 0.25
                                                            }}
                                                        >
                                                            {prop.label !== 'Product Name' && prop.label !== 'Price' && (
                                                                <strong>{prop.label}: </strong>
                                                            )}
                                                            {value}
                                                        </Typography>
                                                    );
                                                })}
                                            </Box>
                                        }
                                        secondary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleQuantityChange(item.productId, item.quantity - 1)}
                                                    disabled={item.quantity <= 1}
                                                >
                                                    <RemoveIcon />
                                                </IconButton>
                                                <Typography variant="body2" sx={{ minWidth: 20, textAlign: 'center' }}>
                                                    {item.quantity}
                                                </Typography>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleQuantityChange(item.productId, item.quantity + 1)}
                                                >
                                                    <AddIcon />
                                                </IconButton>
                                            </Box>
                                        }
                                    />
                                    <ListItemSecondaryAction>
                                        <IconButton
                                            edge="end"
                                            color="error"
                                            onClick={() => dispatch(cartActions.removeItem(item.productId))}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                                {index < cart.items.length - 1 && showItemSeparators && (
                                    <Divider sx={{ backgroundColor: itemSeparatorColor }} />
                                )}
                            </React.Fragment>
                            );
                        })}
                    </List>
                    
                    {/* Clear Cart Button */}
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <Button
                            variant="outlined"
                            size="small"
                            color="error"
                            onClick={handleClearCart}
                            disabled={cart.items.length === 0}
                        >
                            Clear Cart
                        </Button>
                    </Box>
                </Box>
            )}

            {/* Customer Information Form */}
            {showCustomerForm && cart.items.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" component="h3" sx={{ mb: 2, fontSize: '1.1rem' }}>
                        {customerFormTitle}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Email"
                            type="email"
                            value={cart.customerInfo.email}
                            onChange={(e) => handleCustomerInfoChange('email', e.target.value)}
                            size="small"
                            fullWidth
                            required
                            InputProps={{
                                startAdornment: <EmailIcon sx={{ mr: 1, color: '#666' }} />
                            }}
                        />
                        
                        <TextField
                            label="Phone"
                            type="tel"
                            value={cart.customerInfo.phone}
                            onChange={(e) => handleCustomerInfoChange('phone', e.target.value)}
                            size="small"
                            fullWidth
                            required
                            InputProps={{
                                startAdornment: <PhoneIcon sx={{ mr: 1, color: '#666' }} />
                            }}
                        />
                        
                        <TextField
                            label="Address"
                            multiline
                            rows={2}
                            value={cart.customerInfo.address}
                            onChange={(e) => handleCustomerInfoChange('address', e.target.value)}
                            size="small"
                            fullWidth
                            InputProps={{
                                startAdornment: <AddressIcon sx={{ mr: 1, color: '#666', alignSelf: 'flex-start', mt: 1 }} />
                            }}
                        />
                    </Box>
                </Box>
            )}

            {/* Order Button */}
            {showOrderButton && cart.items.length > 0 && (
                <Box sx={{ mt: 2 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        fullWidth
                        onClick={handleConfirmOrder}
                        disabled={isCreatingOrder || !cart.customerInfo.email || !cart.customerInfo.phone}
                        startIcon={isCreatingOrder ? null : <ConfirmIcon />}
                        sx={{ py: 1.5 }}
                    >
                        {isCreatingOrder ? 'Creating Order...' : orderButtonText}
                    </Button>
                </Box>
            )}

            {/* Error Display */}
            {orderError && (
                <Alert 
                    severity="error" 
                    onClose={() => setOrderError(null)}
                    sx={{ mt: 2 }}
                >
                    {orderError}
                </Alert>
            )}

            {/* Success Snackbar */}
            <Snackbar
                open={showOrderSuccess}
                autoHideDuration={6000}
                onClose={() => setShowOrderSuccess(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={() => setShowOrderSuccess(false)} 
                    severity="success"
                    sx={{ width: '100%' }}
                >
                    {orderSuccessMessage}
                </Alert>
            </Snackbar>

            {/* Success Modal */}
            <Dialog
                open={showSuccessModal}
                onClose={handleSuccessModalOk}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ textAlign: 'center', color: 'success.main' }}>
                    <ConfirmIcon sx={{ fontSize: 48, mb: 1, display: 'block', mx: 'auto' }} />
                    Order Confirmed!
                </DialogTitle>
                <DialogContent sx={{ textAlign: 'center', pb: 1 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        {orderSuccessMessage}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Thank you for your order. You will receive a confirmation shortly.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                    <Button
                        onClick={handleSuccessModalOk}
                        variant="contained"
                        color="primary"
                        size="large"
                        sx={{ px: 4 }}
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

// Settings component for the Cart
const CartSettings = () => {
    const {
        actions: { setProp },
        title,
        showTitle,
        showItemCount,
        showCustomerForm,
        showOrderButton,
        emptyCartMessage,
        orderButtonText,
        orderSuccessMessage,
        orderDomain,
        itemProperties,
        showItemImages,
        imageSize,
        width,
        maxHeight,
        backgroundColor,
        borderRadius,
        padding,
        showBorder,
        borderColor,
        borderWidth,
        showItemSeparators,
        itemSeparatorColor,
        debug
    } = useNode((node) => ({
        title: node.data.props.title,
        showTitle: node.data.props.showTitle,
        showItemCount: node.data.props.showItemCount,
        showCustomerForm: node.data.props.showCustomerForm,
        showOrderButton: node.data.props.showOrderButton,
        emptyCartMessage: node.data.props.emptyCartMessage,
        orderButtonText: node.data.props.orderButtonText,
        orderSuccessMessage: node.data.props.orderSuccessMessage,
        orderDomain: node.data.props.orderDomain,
        itemProperties: node.data.props.itemProperties,
        showItemImages: node.data.props.showItemImages,
        imageSize: node.data.props.imageSize,
        width: node.data.props.width,
        maxHeight: node.data.props.maxHeight,
        backgroundColor: node.data.props.backgroundColor,
        borderRadius: node.data.props.borderRadius,
        padding: node.data.props.padding,
        showBorder: node.data.props.showBorder,
        borderColor: node.data.props.borderColor,
        borderWidth: node.data.props.borderWidth,
        showItemSeparators: node.data.props.showItemSeparators,
        itemSeparatorColor: node.data.props.itemSeparatorColor,
        debug: node.data.props.debug
    }));

    const [localTitle, setLocalTitle] = useState(title);
    const [localEmptyCartMessage, setLocalEmptyCartMessage] = useState(emptyCartMessage);
    const [localOrderButtonText, setLocalOrderButtonText] = useState(orderButtonText);
    const [localOrderSuccessMessage, setLocalOrderSuccessMessage] = useState(orderSuccessMessage);
    const [localOrderDomain, setLocalOrderDomain] = useState(orderDomain);
    const [localImageSize, setLocalImageSize] = useState(imageSize);
    const [localWidth, setLocalWidth] = useState(width);
    const [localMaxHeight, setLocalMaxHeight] = useState(maxHeight);
    const [localBackgroundColor, setLocalBackgroundColor] = useState(backgroundColor);
    const [localBorderRadius, setLocalBorderRadius] = useState(borderRadius);
    const [localPadding, setLocalPadding] = useState(padding);
    const [localBorderColor, setLocalBorderColor] = useState(borderColor);
    const [localBorderWidth, setLocalBorderWidth] = useState(borderWidth);
    const [localItemSeparatorColor, setLocalItemSeparatorColor] = useState(itemSeparatorColor);
    const [newPropertyField, setNewPropertyField] = useState('');
    const [newPropertyLabel, setNewPropertyLabel] = useState('');

    // Helper function to safely update itemProperties
    const updateItemProperties = (updateFunction) => {
        // Create a completely new array with new objects
        const currentProps = itemProperties || [];
        const newProps = currentProps.map(prop => ({
            field: prop.field,
            label: prop.label,
            enabled: prop.enabled
        }));
        
        // Apply the update function
        const updatedProps = updateFunction(newProps);
        
        // Set the new array
        setProp(props => props.itemProperties = updatedProps);
    };

    return (
        <div>
            {/* Basic Settings */}
            <FormControl fullWidth margin="normal">
                <FormLabel>Cart Title</FormLabel>
                <TextField
                    value={localTitle || ''}
                    onChange={(e) => setLocalTitle(e.target.value)}
                    onBlur={(e) => setProp(props => props.title = e.target.value)}
                    size="small"
                    placeholder="Shopping Cart"
                />
            </FormControl>

            <FormControl fullWidth margin="normal">
                <FormLabel>Order Domain</FormLabel>
                <TextField
                    value={localOrderDomain || ''}
                    onChange={(e) => setLocalOrderDomain(e.target.value)}
                    onBlur={(e) => setProp(props => props.orderDomain = e.target.value)}
                    size="small"
                    placeholder="autodinStockOrder"
                    helperText="Database domain where orders will be saved"
                />
            </FormControl>

            {/* Item Properties Configuration */}
            <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Item Properties Display
                </FormLabel>
                
                <FormControl fullWidth margin="normal">
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showItemImages}
                                onChange={(e) => setProp(props => props.showItemImages = e.target.checked)}
                            />
                        }
                        label="Show Item Images"
                    />
                </FormControl>

                {showItemImages && (
                    <FormControl fullWidth margin="normal">
                        <FormLabel>Image Size</FormLabel>
                        <TextField
                            value={localImageSize || ''}
                            onChange={(e) => setLocalImageSize(e.target.value)}
                            onBlur={(e) => setProp(props => props.imageSize = e.target.value)}
                            size="small"
                            placeholder="40px"
                        />
                    </FormControl>
                )}

                <FormLabel component="legend" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                    Properties to Display
                </FormLabel>

                {itemProperties && itemProperties.map((prop, index) => (
                    <Box key={index} sx={{ border: '1px solid #eee', borderRadius: 1, p: 2, mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={prop.enabled}
                                        onChange={(e) => {
                                            updateItemProperties((props) => {
                                                props[index].enabled = e.target.checked;
                                                return props;
                                            });
                                        }}
                                    />
                                }
                                label="Enable"
                            />
                            <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                    updateItemProperties((props) => {
                                        props.splice(index, 1);
                                        return props;
                                    });
                                }}
                                sx={{ minWidth: 'auto' }}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                                label="Field Name"
                                size="small"
                                value={prop.field}
                                onChange={(e) => {
                                    updateItemProperties((props) => {
                                        props[index].field = e.target.value;
                                        return props;
                                    });
                                }}
                                sx={{ flex: 1 }}
                            />
                            <TextField
                                label="Display Label"
                                size="small"
                                value={prop.label}
                                onChange={(e) => {
                                    updateItemProperties((props) => {
                                        props[index].label = e.target.value;
                                        return props;
                                    });
                                }}
                                sx={{ flex: 1 }}
                            />
                        </Box>
                    </Box>
                ))}

                <Box sx={{ mt: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Add New Property</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <TextField
                            label="Field Name"
                            size="small"
                            placeholder="fieldName"
                            value={newPropertyField}
                            onChange={(e) => setNewPropertyField(e.target.value)}
                            sx={{ flex: 1 }}
                        />
                        <TextField
                            label="Display Label"
                            size="small"
                            placeholder="Display Label"
                            value={newPropertyLabel}
                            onChange={(e) => setNewPropertyLabel(e.target.value)}
                            sx={{ flex: 1 }}
                        />
                    </Box>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                            if (newPropertyField.trim() && newPropertyLabel.trim()) {
                                const newProperty = {
                                    field: newPropertyField.trim(),
                                    label: newPropertyLabel.trim(),
                                    enabled: true
                                };
                                updateItemProperties((props) => {
                                    props.push(newProperty);
                                    return props;
                                });
                                setNewPropertyField('');
                                setNewPropertyLabel('');
                            }
                        }}
                        disabled={!newPropertyField.trim() || !newPropertyLabel.trim()}
                        fullWidth
                    >
                        Add Property
                    </Button>
                </Box>
            </Box>

            {/* Display Settings */}
            <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Display Settings
                </FormLabel>
                
                <FormControl fullWidth margin="normal">
                    <FormLabel>Empty Cart Message</FormLabel>
                    <TextField
                        value={localEmptyCartMessage || ''}
                        onChange={(e) => setLocalEmptyCartMessage(e.target.value)}
                        onBlur={(e) => setProp(props => props.emptyCartMessage = e.target.value)}
                        size="small"
                        placeholder="Your cart is empty"
                    />
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <FormLabel>Order Button Text</FormLabel>
                    <TextField
                        value={localOrderButtonText || ''}
                        onChange={(e) => setLocalOrderButtonText(e.target.value)}
                        onBlur={(e) => setProp(props => props.orderButtonText = e.target.value)}
                        size="small"
                        placeholder="Confirm Order"
                    />
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <FormLabel>Order Success Message</FormLabel>
                    <TextField
                        value={localOrderSuccessMessage || ''}
                        onChange={(e) => setLocalOrderSuccessMessage(e.target.value)}
                        onBlur={(e) => setProp(props => props.orderSuccessMessage = e.target.value)}
                        size="small"
                        placeholder="Order confirmed successfully!"
                    />
                </FormControl>
            </Box>

            {/* Styling */}
            <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Styling
                </FormLabel>
                
                <FormControl fullWidth margin="normal">
                    <FormLabel>Width</FormLabel>
                    <TextField
                        value={localWidth || ''}
                        onChange={(e) => setLocalWidth(e.target.value)}
                        onBlur={(e) => setProp(props => props.width = e.target.value)}
                        size="small"
                        placeholder="400px"
                    />
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <FormLabel>Max Height</FormLabel>
                    <TextField
                        value={localMaxHeight || ''}
                        onChange={(e) => setLocalMaxHeight(e.target.value)}
                        onBlur={(e) => setProp(props => props.maxHeight = e.target.value)}
                        size="small"
                        placeholder="600px"
                    />
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <FormLabel>Background Color</FormLabel>
                    <TextField
                        type="color"
                        value={localBackgroundColor || '#ffffff'}
                        onChange={(e) => setLocalBackgroundColor(e.target.value)}
                        onBlur={(e) => setProp(props => props.backgroundColor = e.target.value)}
                        size="small"
                    />
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <FormLabel>Border Radius</FormLabel>
                    <TextField
                        value={localBorderRadius || ''}
                        onChange={(e) => setLocalBorderRadius(e.target.value)}
                        onBlur={(e) => setProp(props => props.borderRadius = e.target.value)}
                        size="small"
                        placeholder="8px"
                    />
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <FormLabel>Padding</FormLabel>
                    <TextField
                        value={localPadding || ''}
                        onChange={(e) => setLocalPadding(e.target.value)}
                        onBlur={(e) => setProp(props => props.padding = e.target.value)}
                        size="small"
                        placeholder="16px"
                    />
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showBorder}
                                onChange={(e) => setProp(props => props.showBorder = e.target.checked)}
                            />
                        }
                        label="Show Border"
                    />
                </FormControl>

                {showBorder && (
                    <>
                        <FormControl fullWidth margin="normal">
                            <FormLabel>Border Width</FormLabel>
                            <TextField
                                value={localBorderWidth || ''}
                                onChange={(e) => setLocalBorderWidth(e.target.value)}
                                onBlur={(e) => setProp(props => props.borderWidth = e.target.value)}
                                size="small"
                                placeholder="1px"
                            />
                        </FormControl>

                        <FormControl fullWidth margin="normal">
                            <FormLabel>Border Color</FormLabel>
                            <TextField
                                type="color"
                                value={localBorderColor || '#e0e0e0'}
                                onChange={(e) => setLocalBorderColor(e.target.value)}
                                onBlur={(e) => setProp(props => props.borderColor = e.target.value)}
                                size="small"
                            />
                        </FormControl>
                    </>
                )}

                <FormControl fullWidth margin="normal">
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showItemSeparators}
                                onChange={(e) => setProp(props => props.showItemSeparators = e.target.checked)}
                            />
                        }
                        label="Show Item Separators"
                    />
                </FormControl>

                {showItemSeparators && (
                    <FormControl fullWidth margin="normal">
                        <FormLabel>Separator Color</FormLabel>
                        <TextField
                            type="color"
                            value={localItemSeparatorColor || '#f0f0f0'}
                            onChange={(e) => setLocalItemSeparatorColor(e.target.value)}
                            onBlur={(e) => setProp(props => props.itemSeparatorColor = e.target.value)}
                            size="small"
                        />
                    </FormControl>
                )}

                <FormControl fullWidth margin="normal">
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showTitle}
                                onChange={(e) => setProp(props => props.showTitle = e.target.checked)}
                            />
                        }
                        label="Show Title"
                    />
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showItemCount}
                                onChange={(e) => setProp(props => props.showItemCount = e.target.checked)}
                            />
                        }
                        label="Show Item Count"
                    />
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showCustomerForm}
                                onChange={(e) => setProp(props => props.showCustomerForm = e.target.checked)}
                            />
                        }
                        label="Show Customer Form"
                    />
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showOrderButton}
                                onChange={(e) => setProp(props => props.showOrderButton = e.target.checked)}
                            />
                        }
                        label="Show Order Button"
                    />
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <FormControlLabel
                        control={
                            <Switch
                                checked={debug}
                                onChange={(e) => setProp(props => props.debug = e.target.checked)}
                            />
                        }
                        label="Debug Mode (Shows Missing Properties)"
                    />
                </FormControl>
            </Box>
        </div>
    );
};

// Craft.js configuration
Cart.craft = {
    props: {
        title: "Shopping Cart",
        showTitle: true,
        showItemCount: true,
        showCustomerForm: true,
        showOrderButton: true,
        emptyCartMessage: "Your cart is empty",
        orderButtonText: "Confirm Order",
        orderSuccessMessage: "Order confirmed successfully!",
        orderDomain: "autodinStockOrder",
        itemProperties: [
            { field: 'designationcourte', label: 'Product Name', enabled: true },
            { field: 'code', label: 'Code', enabled: true },
            { field: 'deslong', label: 'Description', enabled: true },
            { field: 'famille', label: 'Family', enabled: true },
            { field: 'marque', label: 'Brand', enabled: false },
            { field: 'modele', label: 'Model', enabled: false },
            { field: 'brief', label: 'Brief', enabled: false },
            { field: 'category', label: 'Category', enabled: false }
        ],
        showItemImages: false,
        imageSize: "40px",
        width: "400px",
        maxHeight: "600px",
        backgroundColor: "#ffffff",
        borderRadius: "8px",
        padding: "16px",
        showBorder: true,
        borderColor: "#e0e0e0",
        borderWidth: "1px",
        showItemSeparators: true,
        itemSeparatorColor: "#f0f0f0",
        debug: true
    },
    related: {
        settings: CartSettings
    }
};

export default Cart;